/**
 * Format raw chat messages into a standardized format for display
 *
 * @param {Array} messages - Array of message pairs from the API (each pair is [userMessage, botMessage])
 * @returns {Array} - Formatted messages with sender, content, timestamp, and step
 */
export const formatChatMessages = (messages) => {
  if (!Array.isArray(messages)) {
    console.error('Unexpected message format:', messages)
    return []
  }

  return messages.flatMap((messagePair, pairIndex) => {
    const createsDataTable = messagePair[3] ? true : false

    return [
      {
        sender: 'user',
        content: messagePair[0],
        step: pairIndex,
        createsDataTable: createsDataTable,
      },
      {
        sender: 'bot',
        content: messagePair[1],
        step: pairIndex,
        createsDataTable: createsDataTable,
      },
    ]
  })
}

/**
 * Parse stored message content to extract original message and selected chips
 * @param {string} messageContent - The stored message content with appended chips
 * @param {Object} idToName - Mapping of IDs to names for lookups
 * @returns {Object} - Object with originalMessage and selectedChips
 */
export const parseStoredMessageContent = (messageContent, idToName = {}) => {
  if (!messageContent) {
    return { originalMessage: '', selectedChips: null }
  }

  // Split the message by newlines to find the appended context
  const lines = messageContent.split('\n')
  const originalMessage = lines[0] // First line is always the original message

  const selectedChips = {}

  // Parse each line for chip information
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()

    if (line.startsWith('Selected HPO terms:')) {
      const hpoText = line.replace('Selected HPO terms:', '').trim()
      const hpoIds = hpoText.split(',').map((item) => item.trim().replace('hpo:', ''))
      selectedChips.hpoTerms = hpoIds.map((id) => ({
        hpo_id: id,
        name: idToName.hpo?.[id] || id, // fallback to ID if name not found
      }))
    }

    if (line.startsWith('Selected diseases:')) {
      const diseaseText = line.replace('Selected diseases:', '').trim()
      const diseaseIds = diseaseText.split(',').map((item) => item.trim().replace('disease:', ''))
      selectedChips.diseases = diseaseIds.map((id) => ({
        key: id,
        disease: idToName.disease?.[id] || id, // fallback to ID if name not found
      }))
    }

    if (line.startsWith('Selected samples:')) {
      const samplesText = line.replace('Selected samples:', '').trim()
      const sampleIds = samplesText.split(',').map((item) => item.trim().replace('sample:', ''))
      selectedChips.samples = sampleIds.map((id) => ({
        value: id,
        name: idToName.sample?.[id] || id, // fallback to ID if name not found
      }))
    }

    if (line.startsWith('Selected panels:')) {
      const panelsText = line.replace('Selected panels:', '').trim()
      const panelIds = panelsText.split(',').map((item) => item.trim().replace('panel:', ''))
      selectedChips.panels = panelIds.map((id) => ({
        value: id,
        name: idToName.panel?.[id] || id, // fallback to ID if name not found
      }))
    }
  }

  // Return null if no chips were found
  const hasAnyChips = Object.keys(selectedChips).length > 0

  return {
    originalMessage,
    selectedChips: hasAnyChips ? selectedChips : null,
  }
}

/**
 * Create a standardized user message object
 *
 * @param {string} content - The message content
 * @param {number|null} step - The step number for this message (optional)
 * @returns {Object} - Formatted user message object
 */
export const createUserMessage = (content, step = null) => ({
  sender: 'user',
  content,
  timestamp: new Date().toISOString(),
  step: step,
})

/**
 * Create a standardized bot message object
 *
 * @param {string} content - The message content
 * @param {Object} options - Additional message properties
 * @param {number|null} step - The step number for this message (optional)
 * @returns {Object} - Formatted bot message object
 */
export const createBotMessage = (content, options = {}, step = null) => ({
  // Options are executionPlan and reportVcfId
  sender: 'bot',
  content,
  timestamp: new Date().toISOString(),
  step: step,
  ...options,
})

/**
 * Replace names with their corresponding IDs in a message
 *
 * @param {string} message - The message to process
 * @param {Object} nameToId - An object mapping name types to their corresponding IDs
 * @returns {string} - The message with names replaced by their IDs
 */
export const replaceNamesWithIds = (message, nameToId) => {
  return message.replaceAll(
    /(panel:|sample:|hpo:|disease:)(?:"([^"]+)"|(\S+))/g,
    (match, type, quotedName, unquotedName) => {
      const name = quotedName || unquotedName
      const cleanType = type.replace(':', '')
      const id = nameToId[cleanType]?.[name.trim()]
      return id ? `${type}${id}` : match
    },
  )
}

/**
 * Replace IDs with their corresponding names in a message
 *
 * @param {string} message - The message to process
 * @param {Object} idToName - An object mapping ID types to their corresponding names
 * @returns {string} - The message with IDs replaced by their names
 */
export const replaceIdsWithNames = (message, idToName) => {
  // Handle specific HPO format first (HP:XXXX)
  let result = message.replaceAll(/hpo:(HP:[0-9]+)/g, (match, hpoId) => {
    const name = idToName['hpo']?.[hpoId]
    return name ? `hpo:"${name}"` : match
  })

  // Handle other ID formats (panel:ID, sample:ID, disease:ID, standard hpo:ID)
  return result.replaceAll(/(panel:|sample:|hpo:|disease:)([a-zA-Z0-9_-]+)/g, (match, type, id) => {
    const cleanType = type.replace(':', '')
    const name = idToName[cleanType]?.[id]
    return name ? `${type}"${name}"` : match
  })
}

// Session storage utilities for chatbot
export const getChatbotSessionState = () => {
  try {
    const savedState = sessionStorage.getItem('chatbot_session_state')
    return savedState ? JSON.parse(savedState) : null
  } catch (error) {
    console.error('Error getting chatbot session state:', error)
    return null
  }
}

export const clearChatbotSessionState = () => {
  try {
    sessionStorage.removeItem('chatbot_session_state')
    return true
  } catch (error) {
    console.error('Error clearing chatbot session state:', error)
    return false
  }
}

export const hasChatbotSessionState = () => {
  try {
    const savedState = sessionStorage.getItem('chatbot_session_state')
    return savedState !== null
  } catch (error) {
    console.error('Error checking chatbot session state:', error)
    return false
  }
}
